import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AdminCategories = () => {
  const { user, getToken } = useAuth();
  const getEmptyFormData = (overrides = {}) => ({
    name: '',
    description: '',
    parent_id: '',
    image: '',
    cover_banner_image: '',
    banner_title: '',
    banner_subtitle: '',
    ...overrides
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [formError, setFormError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bannerImagePreview, setBannerImagePreview] = useState(null);
  const [uploadingBannerImage, setUploadingBannerImage] = useState(false);

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (/^https?:\/\//i.test(assetPath)) return assetPath;

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    return `${API_BASE}${normalizedPath}`;
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchCategories();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFormError('Please select an image file');
      return;
    }

    if (file.size > 5000000) {
      setFormError('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    setFormError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          image: data.data.image_url
        }));
        setImagePreview(data.data.image_url);
      } else {
        setFormError(data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setFormError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFormError('Please select an image file'); return; }
    if (file.size > 5000000) { setFormError('Image size should be less than 5MB'); return; }
    setUploadingBannerImage(true);
    setFormError(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken(true)}` },
        body: fd
      });
      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, cover_banner_image: data.data.image_url }));
        setBannerImagePreview(data.data.image_url);
      } else {
        setFormError(data.message || 'Failed to upload banner image');
      }
    } catch (error) {
      setFormError('Failed to upload banner image');
    } finally {
      setUploadingBannerImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    
    try {
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory 
        ? `/api/categories/${editingCategory.id}` 
        : '/api/categories';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken(true)}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh categories to show subcategories
        await fetchCategories();
        resetForm();
        alert(editingCategory ? 'Category updated successfully!' : 'Category added successfully!');
      } else {
        const detail = data.error || data.code || '';
        setFormError(data.message + (detail ? ` (${detail})` : ''));
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setFormError('Network or server error while saving category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData(getEmptyFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
      image: category.image || category.image_url || '',
      cover_banner_image: category.cover_banner_image || '',
      banner_title: category.banner_title || '',
      banner_subtitle: category.banner_subtitle || ''
    }));
    setImagePreview(category.image || category.image_url || null);
    setBannerImagePreview(category.cover_banner_image || null);
    setShowAddForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken(true)}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        fetchCategories();
        alert('Category deleted successfully!');
      } else {
        alert('Error deleting category: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setEditingCategory(null);
    setShowAddForm(false);
    setFormError(null);
    setImagePreview(null);
    setBannerImagePreview(null);
  };

  // Flatten categories to get all main categories for parent selection
  const getMainCategories = () => {
    return categories.filter(cat => !cat.parent_id);
  };

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Category Management</h1>
            <p className="text-gray-600">Manage your fancy item categories</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Add New Category
          </button>
        </div>

        {/* Add/Edit Category Form */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    placeholder="e.g. Luxury Watches, Designer Bags, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    placeholder="Describe this category of fancy items..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Parent Category (Optional - Leave empty for main category)
                  </label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Main Category (No Parent)</option>
                    {getMainCategories()
                      .filter(cat => !editingCategory || cat.id !== editingCategory.id) // Don't allow selecting self as parent
                      .map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.parent_id ? 'This will be a subcategory' : 'This will be a main category'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category Image
                  </label>
                  <div className="space-y-3">
                    {imagePreview && (
                      <div className="relative w-full h-48 border border-gray-200 rounded-xl overflow-hidden">
                        <img 
                          src={getAssetUrl(imagePreview)} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData(prev => ({ ...prev, image: '' }));
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    />
                    {uploadingImage && (
                      <p className="text-xs text-gray-500">Uploading image...</p>
                    )}
                  </div>
                </div>

                {/* Category Page Banner Section */}
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide">Category Page Banner</h3>
                  <p className="text-xs text-gray-500 mb-4">This banner appears at the top of the category page when customers browse this category.</p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image</label>
                      <div className="space-y-3">
                        {bannerImagePreview && (
                          <div className="relative w-full h-40 border border-gray-200 rounded-xl overflow-hidden">
                            <img src={getAssetUrl(bannerImagePreview)} alt="Banner Preview" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => { setBannerImagePreview(null); setFormData(prev => ({ ...prev, cover_banner_image: '' })); }}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        )}
                        <input type="file" accept="image/*" onChange={handleBannerImageUpload} disabled={uploadingBannerImage}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300" />
                        {uploadingBannerImage && <p className="text-xs text-gray-500">Uploading banner image...</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Title</label>
                      <input type="text" name="banner_title" value={formData.banner_title} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        placeholder="e.g. Bags Collection" />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Subtitle</label>
                      <textarea name="banner_subtitle" value={formData.banner_subtitle} onChange={handleInputChange} rows={2}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        placeholder="e.g. Explore our full range of bags and handbags" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 w-full pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105"
                  >
                    {editingCategory ? 'Update Category' : 'Add Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Categories List - Hierarchical Display */}
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin absolute top-0 left-0 animate-pulse"></div>
              </div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-gray-400 mb-4">
                <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No Categories Found</h3>
              <p className="text-gray-500 mb-6">Start by creating your first fancy item category</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300"
              >
                Add First Category
              </button>
            </div>
          ) : (
            getMainCategories().map((mainCategory, index) => (
              <div 
                key={mainCategory.id} 
                className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden border border-white/20"
              >
                {/* Main Category */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {mainCategory.image && (
                          <img 
                            src={getAssetUrl(mainCategory.image)} 
                            alt={mainCategory.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">
                            {mainCategory.name}
                          </h3>
                          <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold inline-block mt-1">
                            Main Category
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">
                        {mainCategory.description || 'No description provided'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {mainCategory.product_count || 0} items
                        {mainCategory.subcategories && mainCategory.subcategories.length > 0 && (
                          <span className="ml-3">
                            • {mainCategory.subcategories.length} subcategories
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setFormData(getEmptyFormData({ parent_id: mainCategory.id }));
                          setEditingCategory(null);
                          setImagePreview(null);
                          setFormError(null);
                          setShowAddForm(true);
                        }}
                        className="text-green-600 hover:text-green-800 font-medium text-sm transition-colors duration-300"
                        title="Add Subcategory"
                      >
                        + Add Subcategory
                      </button>
                      <button
                        onClick={() => handleEdit(mainCategory)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors duration-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(mainCategory.id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors duration-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subcategories */}
                {mainCategory.subcategories && mainCategory.subcategories.length > 0 && (
                  <div className="p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Subcategories:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {mainCategory.subcategories.map((subcategory) => (
                        <div 
                          key={subcategory.id}
                          className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {subcategory.image && (
                                  <img 
                                    src={getAssetUrl(subcategory.image)} 
                                    alt={subcategory.name}
                                    className="w-10 h-10 object-cover rounded"
                                  />
                                )}
                                <h5 className="font-semibold text-gray-900 text-sm">
                                  {subcategory.name}
                                </h5>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {subcategory.description || 'No description'}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                {subcategory.product_count || 0} items
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleEdit(subcategory)}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(subcategory.id)}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;